import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewChild,
  signal
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Subscription, from, take } from 'rxjs';
import { LOAD_BALANCER_TIMEOUT_MS } from 'src/app/shared/vive-api/vive-api.config';

import { takeConnectToken } from '../../connect-token';
import { User } from '../classes/models';
import { AuthService } from '../services/auth.service';
import { SsoService } from '../services/sso.service';
import { ToastService } from '../services/toast.service';
import { VendorService } from '../services/vendor.service';
import {
  SSO_SIGN_IN_REQUIRED_COPY,
  isSsoSignInRequired,
  messageOf
} from '../services/vive-api-errors';
import { SsoLoginButtonComponent } from '../shared-components/sso-login-button/sso-login-button.component';
import { authService as viveAuthService } from '../shared/vive-api/auth.service';
import { emailValidator } from '../validators/email-validator';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class LoginComponent implements OnInit {
  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private VendorService: VendorService,
    private ToastService: ToastService,
    private Location: Location,
    private ssoService: SsoService
  ) {}

  loginObj = { userEmail: '', userPassword: '' };
  loginForm!: FormGroup;
  loginSubscription!: Subscription;

  User?: User;
  @ViewChild('ssoLogin') private ssoLogin?: SsoLoginButtonComponent;
  // Set when the API refuses this page's password because the account signs in
  // with SSO; it shares the alert a failed SSO return uses.
  private readonly passwordRefusal = signal<string | null>(null);

  routeByUser() {
    if (!this.User) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error processing User information.',
        { returnError: true }
      );
    }

    const { UserAccountRole } = this.User;

    if (!UserAccountRole) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error processing User information.',
        { returnError: true }
      );
    }

    const { role } = UserAccountRole;

    const accountTypeName = this.User.UserAccountType?.name ?? '';

    if (accountTypeName === 'Vendor') {
      return this.router.navigate([
        accountTypeName.toLowerCase(),
        this.authService.getUserVendorId(),
        'dashboard'
      ]);
    }

    const NavParts: (string | number)[] = [];

    if (accountTypeName === 'Client') {
      NavParts.push('dashboard');
      // if (role === 'CLIENT_ADMIN') {
      //   NavParts.push('dashboard');
      // }

      // if (role === 'CLIENT_VENDOR_AND_SITES_MANAGER') {
      //   NavParts.push('vendors', 'client');
      // }

      return this.router.navigate([
        accountTypeName.toLowerCase(),
        this.authService.getUserCompanyId(),
        ...NavParts
      ]);
    }
    // If not a Vendor or Client, route to the Run Vive All Vendors Grid.
    return this.router.navigate([
      accountTypeName.toLowerCase(),
      'run-vive',
      { viveStatus: '' }
    ]);
  }

  async handleRouting() {
    if (!this.User) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error processing User information.',
        { returnError: true }
      );
    }

    const accountTypeName = this.User.UserAccountType?.name ?? '';

    const paramMap: ParamMap = this.route.snapshot.paramMap;
    const vendorSignUpCode: string | null = paramMap.get('vendorSignUpCode');
    const claimCode: string | null = paramMap.get('claimCode');
    if (accountTypeName) {
      // If the user logging in is a Vendor and a Client's VendorSignUpCode is in the url, attempt to create
      // a Client/Vendor profile if it doesn't already exist.
      if (accountTypeName === 'Vendor' && vendorSignUpCode) {
        return this.VendorService.clientSignup(
          this.authService.getUserVendorId(),
          vendorSignUpCode,
          { claimCode }
        ).subscribe({
          next: () =>
            this.router.navigate([
              accountTypeName.toLowerCase(),
              this.authService.getUserVendorId(),
              'dashboard'
            ]),
          error: () => {
            /* NEED TO DISPLAY ERROR MESSAGE */
          }
        });
      }

      const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
      if (
        returnUrl &&
        returnUrl.startsWith('/') &&
        !returnUrl.includes('://')
      ) {
        const allowedSegments: Partial<Record<string, string[]>> = {
          Vendor: ['vendor', 'documents'],
          Client: ['client'],
          Admin: ['admin', 'vendor', 'client', 'documents']
        };
        const firstSegment = returnUrl.split('/')[1];
        const allowed = allowedSegments[accountTypeName] ?? [];
        if (allowed.includes(firstSegment)) {
          return this.router.navigateByUrl(returnUrl);
        }
        this.ToastService.addErrorToast(
          'Access Denied',
          "You don't have permission to access the requested page."
        );
      }

      return this.routeByUser();
    }
    return this.router.navigate(['login']);
  }

  public ssoErrorMessage(): string | null {
    return this.passwordRefusal() ?? this.ssoService.errorMessage();
  }

  forgotPassword() {
    this.router.navigate(['login', 'forgot-password']);
  }

  ngOnInit(): void {
    // Need to remove email validator. Angular email validator appears to throw invalid for parenthesis.
    // Should double check this.
    this.loginForm = new FormGroup({
      userEmail: new FormControl(this.loginObj.userEmail, [
        Validators.required,
        emailValidator
      ]),
      userPassword: new FormControl(this.loginObj.userPassword, [
        Validators.required
      ])
    });

    // A Connect link: the head script in index.html already took the token out
    // of the URL. Trade it for this browser's session. A refusal has been
    // toasted by the interceptor with the API's message, and the form stays.
    const connectToken = takeConnectToken();
    if (connectToken) {
      this.loginSubscription = from(
        viveAuthService.exchangeSession({ connectToken })
      )
        .pipe(take(1))
        .subscribe({
          next: (session) => {
            this.authService.seedSession(session);
            this.User = this.authService.getUser();
            this.handleRouting();
          },
          error: () => undefined
        });
      return;
    }

    if (this.authService.isAuthenticated()) {
      this.User = this.authService.getUser();
      this.handleRouting();
    }
  }

  login(): void {
    this.ssoService.clearError();
    this.passwordRefusal.set(null);
    this.ssoLogin?.discover();
    this.loginSubscription = from(
      viveAuthService.createSession({
        data: {
          email: this.loginForm.get('userEmail')?.value,
          password: this.loginForm.get('userPassword')?.value
        },
        options: {
          requestTimeout: LOAD_BALANCER_TIMEOUT_MS,
          context: { ManuallyHandleError: true }
        }
      })
    )
      .pipe(take(1))
      .subscribe({
        next: (session) => {
          this.authService.seedSession(session);
          this.User = this.authService.getUser();
          this.handleRouting();
        },
        error: (error: unknown) => {
          if (isSsoSignInRequired(error)) {
            this.passwordRefusal.set(SSO_SIGN_IN_REQUIRED_COPY);
            return;
          }
          throw this.ToastService.addErrorToast(
            'Error',
            messageOf(error) ?? 'An Error has occurred.',
            { returnError: true }
          );
        }
      });
  }

  ngOnDestroy(): void {
    // The message belongs to the page load that carried ?sso_error=. The service
    // is a root singleton, so leaving /login (Forgot password, or a signed-in
    // visitor routed on) must end it or it reappears on the next visit.
    this.ssoService.clearError();
    if (this.loginSubscription) {
      this.loginSubscription.unsubscribe();
    }
  }
}
